import React from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  return (
    <div className="home-page">
      <nav className="home-nav" aria-label="Home navigation">
        <div className="home-nav__inner">
          <Link to="/"><img src="/logo.png" alt="ProZpects" className="home-nav__logo" /></Link>
          <div className="home-nav__links">
            <Link to="/login" className="home-nav__link home-nav__link--login">Login</Link>
            <Link to="/register" className="home-nav__link home-nav__link--signup">Sign Up</Link>
          </div>
        </div>
      </nav>

      <main className="privacy-policy">
        <div className="privacy-policy__inner">
          <h1>Privacy Policy</h1>
          <p className="privacy-policy__effective">Effective Date: September 16, 2026</p>

          <p>
            ProZpects Recruiting Solutions LLC ("ProZpects," "we," "us," or "our") operates the
            ProZpects platform, including the website at app.prozpects.com and the ProZpects mobile
            applications for iOS and Android (collectively, the "Service"). This Privacy Policy
            explains how we collect, use, disclose, and protect your information when you use our
            Service.
          </p>

          <h2>1. Information We Collect</h2>

          <h3>Account Information</h3>
          <p>When you register for an account, we collect:</p>
          <ul>
            <li>Name, email address, and phone number</li>
            <li>Role selection (athlete/parent or coach)</li>
            <li>Password (stored securely using industry-standard hashing)</li>
          </ul>

          <h3>Athlete Profile Data</h3>
          <p>Athletes and their parents may provide:</p>
          <ul>
            <li>School name, graduation year, and location</li>
            <li>Sport(s) and position(s)</li>
            <li>Athletic measurables (height, weight, 40-yard dash, vertical jump, etc.)</li>
            <li>Game statistics and academic information (GPA, test scores)</li>
            <li>Photos and highlight videos</li>
            <li>Social media links</li>
          </ul>

          <h3>Coach Profile Data</h3>
          <p>Coaches may provide:</p>
          <ul>
            <li>School or university affiliation</li>
            <li>Position title and coaching credentials</li>
            <li>Program information</li>
          </ul>

          <h3>Payment Information</h3>
          <p>
            Payment processing is handled entirely by Stripe, Inc. We do not store your credit card
            number or bank account details on our servers. Stripe collects and processes payment
            information in accordance with their own privacy policy.
          </p>

          <h3>Automatically Collected Information</h3>
          <p>When you use our Service, we may automatically collect:</p>
          <ul>
            <li>Device information (device type, operating system, app version)</li>
            <li>Log data (IP address, access times, pages viewed)</li>
            <li>Usage data (features used, interactions within the app)</li>
          </ul>

          <h2>2. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Create and manage your account</li>
            <li>Display athlete profiles to college coaches searching for recruits</li>
            <li>Facilitate messaging between coaches and athletes</li>
            <li>Process subscription payments through Stripe</li>
            <li>Send transactional emails (account verification, password resets, notifications)</li>
            <li>Verify athletic measurables and statistics through authorized partners</li>
            <li>Improve and maintain the Service</li>
          </ul>

          <h2>3. Text Messaging (SMS)</h2>
          <p>
            If you provide a mobile phone number, you agree that ProZpects may send you text
            (SMS/MMS) messages related to your account and recruiting. Categories include:
          </p>
          <ul>
            <li>Account messages — verification codes and account notifications</li>
            <li>Recruiting and profile updates — new messages, profile activity, and scheduling</li>
            <li>Camps, webinars, and events — registration confirmations and reminders</li>
            <li>Concierge messages — two-way texts with ProZpects staff who help manage your recruiting</li>
          </ul>
          <p>
            Concierge messages may be sent as a group text that includes the athlete, their parents
            or guardians, and their assigned concierge manager; everyone in the group can see the
            other members' phone numbers.
          </p>
          <p>
            Message frequency varies. <strong>Message and data rates may apply.</strong> You can opt
            out at any time by replying <strong>STOP</strong> to any message, and reply{' '}
            <strong>HELP</strong> for help. Opting out will not affect your ability to use the core
            Service, though you may miss time-sensitive recruiting and account notifications.
          </p>
          <p>
            <strong>
              We do not sell, rent, or share your mobile phone number, SMS opt-in, or consent
              information with any third parties or affiliates for any purpose.
            </strong>{' '}
            Mobile opt-in data is used solely to deliver the messages described above. Text
            messaging originator opt-in data and consent are never shared with any third parties.
          </p>

          <h2>4. How Your Information Is Shared</h2>

          <h3>Profile Visibility</h3>
          <p>
            Athlete profiles are visible to registered coaches through the platform's search
            feature regardless of subscription status. This is a core function of the Service —
            connecting athletes with recruiting opportunities.
          </p>

          <h3>Third-Party Services</h3>
          <p>We use the following third-party services to operate the platform:</p>
          <ul>
            <li><strong>Stripe</strong> — Payment processing for subscriptions</li>
            <li><strong>Google OAuth</strong> — Optional sign-in authentication</li>
            <li><strong>Amazon Web Services (AWS S3)</strong> — Secure storage for uploaded photos, videos, and documents</li>
            <li><strong>Gmail SMTP</strong> — Sending transactional emails and notifications</li>
          </ul>

          <h3>We Do Not Sell Your Data</h3>
          <p>
            We do not sell, rent, or trade your personal information to third parties for marketing
            purposes. We only share data as described in this policy or as required by law.
          </p>

          <h2>5. Data Retention and Deletion</h2>
          <p>
            We retain your personal information for as long as your account is active or as needed
            to provide you the Service. You may request deletion of your account and associated data
            by contacting us at the email address below.
          </p>
          <p>Upon account deletion:</p>
          <ul>
            <li>Your profile will be removed from coach search results immediately</li>
            <li>Your personal data will be deleted from our active systems</li>
            <li>Uploaded media files (photos, videos) will be removed from storage</li>
            <li>Message history with other users will be removed</li>
            <li>Some data may be retained in encrypted backups for up to 90 days before permanent deletion</li>
          </ul>

          <h2>6. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal
            information, including:
          </p>
          <ul>
            <li>HTTPS encryption for all data in transit</li>
            <li>Secure password hashing</li>
            <li>JWT-based authentication with token expiration</li>
            <li>Encrypted storage for sensitive credentials</li>
          </ul>
          <p>
            While we strive to protect your information, no method of electronic storage or
            transmission is 100% secure. We cannot guarantee absolute security.
          </p>

          <h2>7. Children's Privacy</h2>
          <p>
            Our Service is not directed to children under the age of 13. We do not knowingly collect
            personal information from children under 13. If you are a parent or guardian and believe
            your child under 13 has provided us with personal information, please contact us and we
            will take steps to delete such information.
          </p>
          <p>
            Athletes between 13 and 18 should have a parent or guardian create and manage their
            account on their behalf using the parent/athlete account type.
          </p>

          <h2>8. Your Rights</h2>
          <p>Depending on your location, you may have the right to:</p>
          <ul>
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your personal information</li>
            <li>Object to or restrict certain processing of your data</li>
            <li>Request a copy of your data in a portable format</li>
          </ul>
          <p>To exercise any of these rights, please contact us using the information below.</p>

          <h2>9. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material
            changes by posting the updated policy on this page and updating the "Effective Date"
            above. We encourage you to review this page periodically. Continued use of the Service
            after changes constitutes acceptance of the updated policy.
          </p>

          <h2>10. Contact Us</h2>
          <p>
            If you have questions or concerns about this Privacy Policy or our data practices,
            please contact us at:
          </p>
          <p>
            ProZpects Recruiting Solutions LLC<br />
            Email: <a href="mailto:support@prozpects.com">support@prozpects.com</a>
          </p>
        </div>
      </main>

      <footer className="home-footer">
        <div className="home-footer__inner">
          <p>&copy; 2026 ProZpects Recruiting Solutions LLC. All rights reserved.</p>
          <p>
            <Link to="/privacy" className="home-footer__link">Privacy Policy</Link>
            {' · '}
            <Link to="/terms" className="home-footer__link">Terms of Use</Link>
            {' · '}
            <Link to="/support" className="home-footer__link">Support</Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
